import React, { useState, useEffect } from 'react';

const EVENT_TYPES = ['charity', 'community', 'raid-train', 'game-night', 'tournament'];
const EVENT_STATUSES = ['upcoming', 'live', 'completed'];

const DEFAULT_EVENT = {
  title: '',
  date: '',
  time: '',
  type: 'community',
  description: '',
  host: 'The Canopy Team',
  participants: '',
  status: 'upcoming',
  link: '',
  tags: []
};

function EventsEditor({ data, onSave, showToast }) {
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (data) {
      setEvents(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  const handleAddEvent = (date = null) => {
    const newEvent = { ...DEFAULT_EVENT };
    if (date) {
      newEvent.date = formatDate(date);
    }
    setEditingEvent({ ...newEvent, isNew: true });
    setShowModal(true);
  };

  const handleEditEvent = (event, index) => {
    setEditingEvent({ ...event, index, isNew: false });
    setShowModal(true);
  };

  const handleDeleteEvent = (index) => {
    const event = events[index];
    if (!window.confirm(`Delete "${event.title}"?`)) {
      return;
    }
    const updated = events.filter((_, i) => i !== index);
    setEvents(updated);
    setHasChanges(true);
    showToast('Event removed. Click Save to confirm.', 'warning');
  };

  const handleSaveEvent = (eventData) => {
    let updated;
    if (eventData.isNew) {
      const { isNew, index, ...cleanEvent } = eventData;
      updated = [...events, cleanEvent];
    } else {
      updated = events.map((e, i) => {
        if (i === eventData.index) {
          const { isNew, index, ...cleanEvent } = eventData;
          return cleanEvent;
        }
        return e;
      });
    }

    // Sort by date
    updated.sort((a, b) => new Date(a.date) - new Date(b.date));

    setEvents(updated);
    setShowModal(false);
    setEditingEvent(null);
    setHasChanges(true);
    showToast(`Event ${eventData.isNew ? 'added' : 'updated'}. Click Save to confirm.`, 'warning');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const result = await onSave(events);
    setSaving(false);
    if (result.success) {
      setHasChanges(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    return events.filter(e => e.date === dateStr);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--forest-800)', margin: 0 }}>
            Events
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--forest-600)', marginTop: '0.25rem' }}>
            {events.length} events
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ display: 'flex', background: 'var(--forest-100)', borderRadius: '0.5rem', padding: '0.25rem' }}>
            <button
              className={`btn-editor btn-editor-ghost ${viewMode === 'list' ? 'btn-editor-secondary' : ''}`}
              onClick={() => setViewMode('list')}
              style={{ padding: '0.375rem 0.75rem' }}
            >
              List
            </button>
            <button
              className={`btn-editor btn-editor-ghost ${viewMode === 'calendar' ? 'btn-editor-secondary' : ''}`}
              onClick={() => setViewMode('calendar')}
              style={{ padding: '0.375rem 0.75rem' }}
            >
              Calendar
            </button>
          </div>
          <button className="btn-editor btn-editor-secondary" onClick={() => handleAddEvent()}>
            + Add Event
          </button>
          <button
            className="btn-editor btn-editor-primary"
            onClick={handleSaveAll}
            disabled={!hasChanges || saving}
            style={{ opacity: hasChanges ? 1 : 0.5 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* View */}
      {viewMode === 'calendar' ? (
        <CalendarView
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          events={events}
          getEventsForDate={getEventsForDate}
          onDateClick={handleAddEvent}
          onEventClick={(event, index) => handleEditEvent(event, index)}
        />
      ) : (
        <ListView
          events={events}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
          onAdd={() => handleAddEvent()}
        />
      )}

      {/* Modal */}
      {showModal && editingEvent && (
        <EventModal
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}

function ListView({ events, onEdit, onDelete, onAdd }) {
  const upcomingEvents = events.filter(e => e.status === 'upcoming' || e.status === 'live');
  const pastEvents = events.filter(e => e.status === 'completed');

  if (events.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">Cal</div>
        <div className="empty-state-title">No events yet</div>
        <div className="empty-state-text">Create your first event to get started</div>
        <button className="btn-editor btn-editor-primary" onClick={onAdd}>
          + Add Event
        </button>
      </div>
    );
  }

  return (
    <div>
      {upcomingEvents.length > 0 && (
        <div className="role-section">
          <div className="role-header">
            <h3 className="role-title">Upcoming Events</h3>
          </div>
          <div className="content-grid">
            {upcomingEvents.map((event, idx) => {
              const originalIndex = events.indexOf(event);
              return (
                <EventCard
                  key={idx}
                  event={event}
                  onEdit={() => onEdit(event, originalIndex)}
                  onDelete={() => onDelete(originalIndex)}
                />
              );
            })}
          </div>
        </div>
      )}

      {pastEvents.length > 0 && (
        <div className="role-section">
          <div className="role-header">
            <h3 className="role-title">Past Events</h3>
          </div>
          <div className="content-grid">
            {pastEvents.map((event, idx) => {
              const originalIndex = events.indexOf(event);
              return (
                <EventCard
                  key={idx}
                  event={event}
                  onEdit={() => onEdit(event, originalIndex)}
                  onDelete={() => onDelete(originalIndex)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EventCard({ event, onEdit, onDelete }) {
  return (
    <div className="content-card">
      <div className="content-card-header">
        <div className="content-card-info" style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span className={`event-tag ${event.type}`}>{event.type}</span>
            {event.status === 'live' && <span className="live-badge">LIVE</span>}
          </div>
          <div className="content-card-name">{event.title}</div>
          <div className="content-card-role">{event.date} at {event.time}</div>
        </div>
        <div className="content-card-actions">
          <button className="btn-editor btn-icon btn-editor-ghost" onClick={onEdit}>Edit</button>
          <button className="btn-editor btn-icon btn-editor-danger" onClick={onDelete}>X</button>
        </div>
      </div>
      {event.description && (
        <div className="content-card-body" style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {event.description}
        </div>
      )}
      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--forest-500)' }}>
        Host: {event.host}
      </div>
    </div>
  );
}

function CalendarView({ currentMonth, setCurrentMonth, events, getEventsForDate, onDateClick, onEventClick }) {
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add days from previous month
    const startDay = firstDay.getDay();
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, otherMonth: true });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), otherMonth: false });
    }

    // Add days from next month
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), otherMonth: true });
    }

    return days;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const today = new Date();
  const days = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button className="btn-editor btn-editor-ghost" onClick={prevMonth}>&lt;</button>
        <h3 className="calendar-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button className="btn-editor btn-editor-ghost" onClick={nextMonth}>&gt;</button>
      </div>

      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}

        {days.map((day, idx) => {
          const isToday = day.date.toDateString() === today.toDateString();
          const dayEvents = getEventsForDate(day.date);
          return (
            <div
              key={idx}
              className={`calendar-day ${isToday ? 'today' : ''} ${day.otherMonth ? 'other-month' : ''} ${dayEvents.length > 0 ? 'has-event' : ''}`}
              onClick={() => onDateClick(day.date)}
            >
              {day.date.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventModal({ event, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: event.title || '',
    date: event.date || '',
    time: event.time || '7:00 PM EST',
    type: event.type || 'community',
    description: event.description || '',
    host: event.host || 'The Canopy Team',
    participants: event.participants || '',
    status: event.status || 'upcoming',
    link: event.link || '',
    tags: event.tags || [],
    isNew: event.isNew,
    index: event.index
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.date.trim()) newErrors.date = 'Date is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.host.trim()) newErrors.host = 'Host is required';
    if (formData.link && !formData.link.match(/^https?:\/\/.+/)) {
      newErrors.link = 'Must be a valid URL';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{event.isNew ? 'Add Event' : 'Edit Event'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Event Title *</label>
              <input
                type="text"
                className={`form-input ${errors.title ? 'error' : ''}`}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Holiday Charity Stream"
              />
              {errors.title && <div className="form-error">{errors.title}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="text"
                  className={`form-input ${errors.date ? 'error' : ''}`}
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  placeholder="Dec 15, 2024"
                />
                {errors.date && <div className="form-error">{errors.date}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Time</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  placeholder="7:00 PM EST"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-input form-select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-input form-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {EVENT_STATUSES.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the event..."
                rows={3}
              />
              {errors.description && <div className="form-error">{errors.description}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Host *</label>
                <input
                  type="text"
                  className={`form-input ${errors.host ? 'error' : ''}`}
                  value={formData.host}
                  onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                  placeholder="The Canopy Team"
                />
                {errors.host && <div className="form-error">{errors.host}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Participants</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.participants}
                  onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                  placeholder="All Team Members"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Link (optional)</label>
              <input
                type="text"
                className={`form-input ${errors.link ? 'error' : ''}`}
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://twitch.tv/..."
              />
              {errors.link && <div className="form-error">{errors.link}</div>}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-editor btn-editor-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-editor btn-editor-primary">
              {event.isNew ? 'Add Event' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventsEditor;
